import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import IconInfoOrenge from '../../assets/images/common/icon-info-orange.svg';
import IconSend from '../../assets/images/common/icon-send.svg';
import ArrowBackBlue from '../../assets/images/common/arrow-back-blue.svg';

import { TS_SCREEN, SCREEN_NAME } from '../../types/common/CommonType';
import { ErrorMessages } from '../../types/common/Messages';
import { CONFIRMATIONS_TITLE } from '../../types/common/ConfirmationsType';
import { ContactInputType } from '../../types/common/ContactType';
import { CustomerInformationInputType } from '../../types/common/CustomerInformationType';
import { STARTUP_API_ERRORS } from '../../types/api/StartApiType';
import { INQUIRY_API_ERRORS } from '../../types/api/InquiryApiType';
import { ESTIMATE_API_ERRORS } from '../../types/api/EstimateApiType';

import { ConfirmationEstimation } from './ConfirmationEstimation';
import { ConfirmationContact } from './ConfirmationContact';
import { ConfirmationCustomerInformation } from './ConfirmationCustomerInformation';
import { ConfirmationLogic } from './ConfirmationLogic';
import { ErrorModalModal } from '../../components/ErrorModal';
import { inquiryApi } from '../../api/inquiry';
import { addRow as setMoveScreen } from '../../slice/ScreenHistorySlice';
import { useAppSelector, useAppDispatch } from '../../store/hooks';
import { EditEstimateRowLogic } from '../../pages/EditEstimate/editEstimateRowLogic';
import { estimateApi } from '../../api/estimate';

type ConfirmationProps = {
    contactInput: ContactInputType; // 問い合わせ入力情報
    setContactInput: (value: ContactInputType) => void; // 問い合わせ入力情報setState
    cIInput: CustomerInformationInputType; // 個人情報入力情報
    setCIInput: (value: CustomerInformationInputType) => void; // 個人情報入力情報setState
    setReceiptNumber: (value: string) => void; // 受付番号setState
};

// 確認画面
export const Confirmation: React.FC<ConfirmationProps> = ({
    setReceiptNumber,
    contactInput,
    cIInput,
    setCIInput,
    setContactInput,
}) => {
    const navigation = useNavigate();
    const dispatch = useAppDispatch();
    const { executeRecaptcha } = useGoogleReCaptcha();

    // 遷移元情報
    const screenTransition = useAppSelector((s) => s.ScreenTransition);
    // 遷移履歴
    const screenHistory = useAppSelector((s) => s.ScreenHistory);
    // client-id
    const clientId = useAppSelector((s) => s.ClientId);
    // 都道府県、市区群マスタ
    const masterPrefecture = useAppSelector((s) => s.MasterPrefecture);
    // 保守レベルマスタ
    const masterSla = useAppSelector((s) => s.MasterSla);
    //
    const masterInquireData = useAppSelector((s) => s.MasterInquire);
    // 見積り問合せ
    const estimateInquiry = useAppSelector((s) => s.EstimateInquiry);
    // state更新方法のコレクションを生成
    const setState = EditEstimateRowLogic.generateSetState(dispatch, estimateInquiry);

    // 各エラー管理
    const [contactInquireErrorMessage, setContactInquireErrorMessage] = useState<string>('');
    const [contactTextErrorMessage, setContactTextErrorMessage] = useState<string>('');
    const [ciErrorMessages, setCiErrorMessages] = useState<Array<string>>([]);
    const [errorModalMessages, setErrorModalMessages] = useState<Array<string>>([]);
    const [openErrorlModal, setOpenErrorModal] = useState<boolean>(false);
    // ボタン実行管理
    const [buttonPush, setButtonPush] = useState<boolean>(false);

    useEffect(() => {
        // 直前画面が個人情報入力以外の場合は不正遷移と判断
        if (screenHistory[0] === SCREEN_NAME.CUSTOMER_INFORMATION) {
            // 画面遷移ルート追加
            dispatch(setMoveScreen(SCREEN_NAME.CONFIRMATION));
        } else {
            // 不正遷移の場合は検索TOPに遷移
            navigation('/');
        }
    }, []);

    // 送信するボタン押下動作
    const sendComplete = () => {
        // 受付番号初期化
        setReceiptNumber('');
        setErrorModalMessages([]);
        if (!buttonPush) {
            const errorMessages = ConfirmationLogic.confirmationValidation(
                screenTransition,
                estimateInquiry,
                contactInput,
                cIInput,
                setContactInquireErrorMessage,
                setContactTextErrorMessage,
                setCiErrorMessages
            );
            if (errorMessages.length > 0) {
                // バリデーションエラー処理
                setErrorModalMessages(errorMessages);
                setOpenErrorModal(true);
            }
            // エラーがあれば以降の処理を行わない。
            else {
                const inquiryImplementation = async () => {
                    // 見積登録結果
                    let estimateApiResult: string | undefined = '';
                    setButtonPush(true);
                    // リキャプチャ実施
                    if (executeRecaptcha) {
                        const token = await executeRecaptcha();
                        const RecaptcharResult = await inquiryApi.sendRecaptcha(token);
                        if (RecaptcharResult === true) {
                            setErrorModalMessages([ErrorMessages.CONFIRM_SEND_ERROR]);
                            setOpenErrorModal(true);
                            return;
                        } else if (RecaptcharResult as string) {
                            setErrorModalMessages([
                                ErrorMessages.API4002_VALD_ERROR.replace('%1', RecaptcharResult as string),
                            ]);
                            setOpenErrorModal(true);
                            return;
                        }
                    }

                    // 見積依頼からの遷移の場合
                    if (screenTransition === TS_SCREEN.EST) {
                        // API-5002
                        estimateApiResult = await estimateApi.saveEstimate(
                            estimateInquiry,
                            dispatch,
                            clientId
                        );
                    }
                    if (estimateApiResult.includes(STARTUP_API_ERRORS.REQUEST_ERROR_8001)) {
                        // 初期APIにてエラー
                        setErrorModalMessages([ErrorMessages.API8001_INIT_ERROR.replace('%1', estimateApiResult)]);
                        setOpenErrorModal(true);
                    } else if (estimateApiResult.includes(ESTIMATE_API_ERRORS.REQUEST_ERROR_5002)) {
                        // 見積り登録APIにてエラー
                        setErrorModalMessages([ErrorMessages.API5002_VALD_ERROR.replace('%1', estimateApiResult)]);
                        setOpenErrorModal(true);
                    } else if (
                        screenTransition === TS_SCREEN.CONTACT ||
                        (screenTransition === TS_SCREEN.EST && estimateApiResult !== '')
                    ) {
                        // 正常系
                        // API-4001
                        const inquiryApiResult = await inquiryApi.inquiryImplementation(
                            screenTransition,
                            cIInput,
                            contactInput,
                            estimateApiResult ?? '',
                            dispatch,
                            clientId
                        );

                        if (inquiryApiResult.includes(STARTUP_API_ERRORS.REQUEST_ERROR_8001)) {
                            // 初期APIにてエラー
                            setErrorModalMessages([ErrorMessages.API8001_INIT_ERROR.replace('%1', inquiryApiResult)]);
                            setOpenErrorModal(true);
                        } else if (inquiryApiResult.includes(INQUIRY_API_ERRORS.REQUEST_ERROR_4001)) {
                            // 問い合わせ登録APIにてエラー
                            setErrorModalMessages([ErrorMessages.API4001_VALD_ERROR.replace('%1', inquiryApiResult)]);
                            setOpenErrorModal(true);
                        } else if (inquiryApiResult) {
                            // API、validation共にエラーではなければ完了画面に遷移
                            // 受付番号登録
                            setReceiptNumber(inquiryApiResult);
                            // 完了画面に遷移
                            navigation('/complete');
                        } else {
                            setErrorModalMessages([ErrorMessages.CONFIRM_SEND_ERROR]);
                            setOpenErrorModal(true);
                        }
                    }
                    setButtonPush(false);
                };
                void inquiryImplementation();
            }
        }
    };

    return (
        <>
            <section className='section page_title up_top_title'>
                <div className='container'>
                    <div className='columns is-desktop'>
                        <div className='column is-6-widescreen-only is-12-mobile page_title-thead'>
                            <h1>
                                {screenTransition === TS_SCREEN.EST
                                    ? CONFIRMATIONS_TITLE.EST
                                    : CONFIRMATIONS_TITLE.CONTACT}
                                　内容確認
                            </h1>
                            <p className='discription'>送信内容にお間違えがないかご確認ください。</p>
                        </div>
                        <div className='column is-6-widescreen-only is-12-mobile'>
                            <div className='alert_box'>
                                <span className='icon'>
                                    <img src={IconInfoOrenge} />
                                </span>
                                <span className='text'>まだ送信は完了していません</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className='section content-wrapper up_top form'>
                <div className='container'>
                    {screenTransition === TS_SCREEN.EST ? (
                        <ConfirmationEstimation
                            estimateInquiry={estimateInquiry}
                            setState={setState}
                            masterPrefecture={masterPrefecture}
                            masterSla={masterSla}
                        />
                    ) : (
                        <ConfirmationContact
                            contactInput={contactInput}
                            setContactInput={setContactInput}
                            inquireErrorMessage={contactInquireErrorMessage}
                            textErrorMessage={contactTextErrorMessage}
                            masterInquireData={masterInquireData}
                        />
                    )}
                </div>
                <ConfirmationCustomerInformation
                    cIInput={cIInput}
                    setCIInput={setCIInput}
                    errerMessages={ciErrorMessages}
                    masterInquireData={masterInquireData}
                />
            </section>
            <section className='section bottom_cta'>
                <div className='container'>
                    <div className='columns'>
                        <div className='column'>
                            <div className='bottom_cta--annotation'>
                                <p className='bottom_cta--annotation_text'>
                                    概算見積では、交換パーツの在庫に問題がない前提で、標準サービス内容にてご提示致します。
                                    <br />
                                    構成パーツ等詳細条件によってはサービスの制約の発生や、価格増減の可能性がありますこと、ご了承ください。
                                </p>
                            </div>
                            <div className='bottom_cta--next'>
                                <p className='bottom_cta--next_text'>
                                    この内容でよろしければ送信ボタンを押してください。
                                </p>
                                <a className='button is-dl_gra' onClick={sendComplete}>
                                    送信する
                                    <span className='icon is-small'>
                                        <img src={IconSend} alt='' />
                                    </span>
                                </a>
                                {screenTransition === TS_SCREEN.EST ? (
                                    <Link to='/editDeviceList' className='button is-buck'>
                                        戻る
                                        <span className='icon is-small'>
                                            <img src={ArrowBackBlue} alt='' />
                                        </span>
                                    </Link>
                                ) : (
                                    <Link to='/contact' className='button is-buck'>
                                        戻る
                                        <span className='icon is-small'>
                                            <img src={ArrowBackBlue} alt='' />
                                        </span>
                                    </Link>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <ErrorModalModal
                errorMessages={errorModalMessages}
                openState={openErrorlModal}
                setOpenState={setOpenErrorModal}
            />
        </>
    );
};
